import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '../components/ui/button';
import { 
    Upload, 
    FileSearch, 
    TrendingUp, 
    MessageCircle, 
    Shield, 
    FileText,
    ArrowRight,
    ArrowLeft,
    CheckCircle2,
    Activity
} from 'lucide-react';
import LanguageSelector from '../components/LanguageSelector';

const LandingPage = () => {
    const { t, i18n } = useTranslation();
    const isRTL = i18n.language === 'ar';
    const ArrowIcon = isRTL ? ArrowLeft : ArrowRight;
    
    const features = [
        {
            icon: Upload,
            title: isRTL ? 'رفع سهل' : 'Easy Upload',
            description: isRTL ? 'ارفع ملفات PDF أو صور التحاليل بسحب وإفلات بسيط' : 'Upload PDF, JPG, or PNG lab reports with simple drag & drop'
        },
        {
            icon: FileSearch,
            title: isRTL ? 'تحليل بالذكاء الاصطناعي' : 'AI Analysis',
            description: isRTL ? 'استخراج وتحليل تلقائي لقيم التحاليل' : 'Automatic extraction and analysis of your lab values'
        },
        {
            icon: Activity,
            title: isRTL ? 'نتائج ملونة' : 'Color-Coded Results',
            description: isRTL ? 'مؤشرات بصرية واضحة للقيم الطبيعية وغير الطبيعية والحرجة' : 'Clear visual indicators for normal, abnormal, and critical values'
        },
        {
            icon: MessageCircle,
            title: isRTL ? 'المساعد الذكي' : 'AI Assistant',
            description: isRTL ? 'اسأل أسئلة عن نتائجك بلغة بسيطة' : 'Ask questions about your results in simple language'
        },
        {
            icon: TrendingUp,
            title: isRTL ? 'تتبع المؤشرات' : 'Track Trends',
            description: isRTL ? 'راقب مؤشراتك الصحية مع الوقت بالرسوم البيانية' : 'Monitor your health metrics over time with charts'
        },
        {
            icon: FileText,
            title: isRTL ? 'تحميل التقارير' : 'Download Reports',
            description: isRTL ? 'صدّر ملخصات PDF احترافية لنتائجك' : 'Export professional PDF summaries of your results'
        }
    ];

    const benefits = [
        isRTL ? 'افهم نتائج تحاليلك بلغة بسيطة' : 'Understand your lab results in plain language',
        isRTL ? 'تتبع مؤشراتك الصحية عبر تقارير متعددة' : 'Track health trends over multiple reports',
        isRTL ? 'احصل على رؤى صحية مدعومة بالذكاء الاصطناعي' : 'Get AI-powered health insights',
        isRTL ? 'بياناتك آمنة وخاصة' : 'Secure and private data handling',
        isRTL ? 'متاح على مدار الساعة من أي جهاز' : 'Available 24/7 from any device'
    ];

    return (
        <div className="min-h-screen" data-testid="landing-page">
            {/* Language Selector - Fixed Position */}
            <div className="fixed top-4 ltr:right-4 rtl:left-4 z-50">
                <LanguageSelector />
            </div>
            
            {/* Hero Section */}
            <section className="hero-gradient relative overflow-hidden">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-32">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                        <div className="animate-slide-up">
                            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-teal-50 text-teal-700 text-sm font-medium mb-6">
                                <Shield className="w-4 h-4" />
                                {isRTL ? 'آمن وخاص' : 'Secure & Private'}
                            </div>
                            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-stone-900 tracking-tight leading-tight mb-6">
                                {isRTL ? 'افهم نتائج' : 'Understand Your'}
                                <span className="block text-teal-600">{isRTL ? 'تحاليلك الطبية' : 'Lab Results'}</span>
                                {isRTL ? 'بالذكاء الاصطناعي' : 'With AI'}
                            </h1>
                            <p className="text-lg text-stone-600 mb-8 max-w-lg leading-relaxed">
                                {isRTL 
                                    ? 'ارفع تقارير تحاليلك الطبية واحصل على تفسيرات واضحة وسهلة الفهم. لا تحتاج لشهادة طبية.'
                                    : 'Upload your medical lab reports and receive clear, easy-to-understand interpretations. No medical degree required.'}
                            </p>
                            <div className="flex flex-col sm:flex-row gap-4">
                                <Link to="/try">
                                    <Button 
                                        size="lg" 
                                        className="bg-teal-500 hover:bg-teal-600 text-white rounded-full px-8 py-6 text-lg shadow-xl shadow-teal-500/25 hover:scale-105 transition-all"
                                        data-testid="hero-try-guest-btn"
                                    >
                                        {isRTL ? 'جرّب الآن بدون تسجيل' : 'Try now — no signup'}
                                        <ArrowIcon className="w-5 h-5 ltr:ml-2 rtl:mr-2" />
                                    </Button>
                                </Link>
                                <Link to="/register">
                                    <Button 
                                        variant="outline"
                                        size="lg" 
                                        className="rounded-full px-8 py-6 text-lg border-2 border-teal-200 text-teal-700 hover:border-teal-500 hover:text-teal-600"
                                        data-testid="hero-get-started-btn"
                                    >
                                        {isRTL ? 'ابدأ مجاناً' : 'Get Started Free'}
                                    </Button>
                                </Link>
                                <Link to="/login">
                                    <Button 
                                        variant="outline" 
                                        size="lg"
                                        className="rounded-full px-8 py-6 text-lg border-2 border-stone-200 hover:border-teal-500 hover:text-teal-600"
                                        data-testid="hero-sign-in-btn"
                                    >
                                        {isRTL ? 'تسجيل الدخول' : 'Sign In'}
                                    </Button>
                                </Link>
                            </div>
                        </div>
                        
                        {/* Hero Image/Illustration */}
                        <div className="relative hidden lg:block animate-fade-in stagger-2">
                            <div className="relative z-10 bg-white rounded-3xl shadow-2xl shadow-stone-200/50 p-6 border border-stone-100">
                                <div className="flex items-center gap-3 mb-4 pb-4 border-b">
                                    <div className="w-10 h-10 rounded-xl bg-teal-500 flex items-center justify-center">
                                        <Activity className="w-5 h-5 text-white" />
                                    </div>
                                    <div>
                                        <p className="font-semibold text-stone-800">{isRTL ? 'تحليل نتائج المختبر' : 'Lab Results Analysis'}</p>
                                        <p className="text-sm text-stone-500">{isRTL ? 'فحص دم شامل' : 'Comprehensive Blood Panel'}</p>
                                    </div>
                                </div>
                                <div className="space-y-3">
                                    {[
                                        { name: isRTL ? 'الهيموجلوبين' : 'Hemoglobin', value: '14.2 g/dL', status: 'normal', statusAr: 'طبيعي' },
                                        { name: isRTL ? 'الجلوكوز' : 'Glucose', value: '105 mg/dL', status: 'abnormal', statusAr: 'مرتفع' },
                                        { name: isRTL ? 'الكولسترول' : 'Cholesterol', value: '185 mg/dL', status: 'normal', statusAr: 'طبيعي' },
                                    ].map((item, i) => (
                                        <div key={i} className={`flex items-center justify-between p-3 rounded-xl ${
                                            item.status === 'normal' ? 'bg-emerald-50' : 'bg-amber-50'
                                        }`}>
                                            <span className="font-medium text-stone-700">{item.name}</span>
                                            <div className="flex items-center gap-2">
                                                <span className={`font-semibold ltr-number ${
                                                    item.status === 'normal' ? 'text-emerald-700' : 'text-amber-700'
                                                }`}>{item.value}</span>
                                                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                                                    item.status === 'normal' 
                                                        ? 'bg-emerald-100 text-emerald-700' 
                                                        : 'bg-amber-100 text-amber-700'
                                                }`}>
                                                    {isRTL ? item.statusAr : item.status}
                                                </span>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                            {/* Decorative elements */}
                            <div className="absolute -top-4 -right-4 w-24 h-24 bg-teal-100 rounded-full blur-2xl opacity-60" />
                            <div className="absolute -bottom-8 -left-8 w-32 h-32 bg-amber-100 rounded-full blur-2xl opacity-60" />
                        </div>
                    </div>
                </div>
            </section>

            {/* Features Section */}
            <section className="py-20 md:py-32 bg-white" data-testid="features-section">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-16">
                        <h2 className="text-3xl sm:text-4xl font-bold text-stone-900 mb-4">
                            {isRTL ? 'كل ما تحتاجه' : 'Everything You Need'}
                        </h2>
                        <p className="text-lg text-stone-600 max-w-2xl mx-auto">
                            {isRTL ? 'منصة شاملة لفهم وتتبع وإدارة بياناتك الصحية' : 'A comprehensive platform to understand, track, and manage your health data'}
                        </p>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {features.map((feature, index) => {
                            const Icon = feature.icon;
                            return (
                                <div 
                                    key={index}
                                    className="group p-6 bg-stone-50 rounded-2xl hover:bg-white hover:shadow-xl hover:shadow-stone-200/50 transition-all duration-300 border border-transparent hover:border-stone-100"
                                    data-testid={`feature-${index}`}
                                >
                                    <div className="w-12 h-12 rounded-xl bg-teal-500/10 flex items-center justify-center mb-4 group-hover:bg-teal-500 group-hover:scale-110 transition-all">
                                        <Icon className="w-6 h-6 text-teal-600 group-hover:text-white transition-colors" />
                                    </div>
                                    <h3 className="text-lg font-semibold text-stone-800 mb-2">{feature.title}</h3>
                                    <p className="text-stone-600">{feature.description}</p>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* How It Works */}
            <section className="py-20 md:py-32 bg-gradient-to-b from-stone-50 to-white" data-testid="how-it-works-section">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-16">
                        <h2 className="text-3xl sm:text-4xl font-bold text-stone-900 mb-4">
                            {isRTL ? 'كيف يعمل' : 'How It Works'}
                        </h2>
                        <p className="text-lg text-stone-600">{isRTL ? 'ثلاث خطوات بسيطة لفهم صحتك' : 'Three simple steps to understand your health'}</p>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                        {[
                            { step: '01', title: isRTL ? 'الرفع' : 'Upload', desc: isRTL ? 'ارفع تقرير التحليل بصيغة PDF أو صورة' : 'Upload your lab report in PDF or image format' },
                            { step: '02', title: isRTL ? 'التحليل' : 'Analyze', desc: isRTL ? 'الذكاء الاصطناعي يستخرج ويحلل قيم التحاليل' : 'AI extracts and analyzes your test values' },
                            { step: '03', title: isRTL ? 'الفهم' : 'Understand', desc: isRTL ? 'احصل على شروحات واضحة ورؤى صحية' : 'Get clear explanations and health insights' }
                        ].map((item, i) => (
                            <div key={i} className="relative text-center">
                                <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-teal-500 text-white font-bold text-xl mb-4 shadow-lg shadow-teal-500/25 ltr-number">
                                    {item.step}
                                </div>
                                <h3 className="text-xl font-semibold text-stone-800 mb-2">{item.title}</h3>
                                <p className="text-stone-600">{item.desc}</p>
                                {i < 2 && (
                                    <div className="hidden md:block absolute top-8 left-[60%] w-[80%] border-t-2 border-dashed border-stone-200" />
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Benefits Section */}
            <section className="py-20 md:py-32 bg-teal-600" data-testid="benefits-section">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                        <div>
                            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
                                Take Control of Your Health Data
                            </h2>
                            <p className="text-teal-100 text-lg mb-8">
                                No more confusion about medical terminology. Get clear insights 
                                into what your lab results really mean.
                            </p>
                            <ul className="space-y-4">
                                {benefits.map((benefit, i) => (
                                    <li key={i} className="flex items-center gap-3 text-white">
                                        <CheckCircle2 className="w-5 h-5 text-teal-300 flex-shrink-0" />
                                        <span>{benefit}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                        <div className="flex justify-center">
                            <Link to="/register">
                                <Button 
                                    size="lg"
                                    className="bg-white text-teal-600 hover:bg-teal-50 rounded-full px-10 py-7 text-lg font-semibold shadow-2xl hover:scale-105 transition-all"
                                    data-testid="cta-get-started-btn"
                                >
                                    Start Analyzing Your Results
                                    <ArrowRight className="ml-2 w-5 h-5" />
                                </Button>
                            </Link>
                        </div>
                    </div>
                </div>
            </section>

            {/* Footer */}
            <footer className="py-12 bg-stone-900" data-testid="footer">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex flex-col md:flex-row items-center justify-between gap-6">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-xl bg-teal-500 flex items-center justify-center">
                                <Activity className="w-5 h-5 text-white" />
                            </div>
                            <div>
                                <span className="text-white font-bold">نتيجتي الطبية</span>
                                <span className="block text-stone-400 text-xs">My Medical Results</span>
                            </div>
                        </div>
                        <div className="flex items-center gap-6 text-stone-400 text-sm">
                            <span>Privacy Policy</span>
                            <span>Terms of Service</span>
                            <span>Contact</span>
                        </div>
                        <p className="text-stone-500 text-sm">
                            © 2025 نتيجتي الطبية. All rights reserved.
                        </p>
                    </div>
                    <div className="mt-8 pt-8 border-t border-stone-800">
                        <p className="text-stone-500 text-sm text-center">
                            ⚠️ Medical Disclaimer: This tool provides educational information only and is not a medical diagnosis. 
                            Always consult with qualified healthcare professionals for medical advice.
                        </p>
                    </div>
                </div>
            </footer>
        </div>
    );
};

export default LandingPage;
